<template>
  <div class="fixed inset-0 z-[500] bg-[var(--p-surface-50)] flex flex-col">
    <!-- Top bar -->
    <div class="flex items-center justify-between h-12 px-4 bg-white border-b border-gray-200 shrink-0">
      <div class="flex items-center gap-3">
        <button @click="exitEditor" class="p-2 rounded-lg text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors">
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
        </button>
        <span class="text-sm font-medium text-gray-900 truncate max-w-[300px]">{{ sourceTitle }}</span>
        <span v-if="mode" class="text-[10px] font-bold uppercase px-2 py-0.5 rounded" :class="mode === 'live' ? 'bg-red-50 text-red-500' : 'bg-violet-50 text-violet-500'">{{ mode }}</span>
      </div>
      <router-link :to="{ name: 'medias' }" class="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-[var(--color-primary)] transition-colors">
        Go to Medias List
        <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
      </router-link>
    </div>

    <!-- Live ended banner — non-blocking. The DVR session id was pinned at
         load, so the editor keeps working against the captured session and
         cuts/exports still resolve. Reloading is only needed to pick up a
         new live content. -->
    <div v-if="liveEnded" class="flex items-center gap-3 px-4 py-2 bg-amber-50 border-b border-amber-100 shrink-0">
      <svg class="w-4 h-4 text-amber-500 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
      <p class="flex-1 text-sm text-amber-700">
        <span class="font-medium">Stream ended.</span>
        You can keep editing the current DVR session. Reload to pick up the new live content.
      </p>
      <button @click="reloadPage" class="inline-flex items-center gap-1.5 px-3 h-7 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium transition-colors">
        <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
        Reload
      </button>
    </div>

    <!-- Not streaming / no session message -->
    <div v-if="liveNotStreaming || liveNoSession" class="flex-1 flex items-center justify-center">
      <div class="text-center max-w-md">
        <div class="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
          <svg class="w-10 h-10 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
            <path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9"/><path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.4"/><circle cx="12" cy="12" r="2"/><path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.4"/><path d="M19.1 4.9C23 8.8 23 15.2 19.1 19.1"/>
          </svg>
        </div>
        <template v-if="liveNoSession">
          <h2 class="text-xl font-semibold text-gray-900 mb-2">No session found</h2>
          <p class="text-sm text-gray-400 mb-6">There's no DVR or recording available for this live yet. If the broadcast just ended, give it a minute and try again.</p>
          <button @click="exitEditor" class="btn-secondary">
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
            Go Back
          </button>
        </template>
        <template v-else>
          <h2 class="text-xl font-semibold text-gray-900 mb-2">{{ mode === 'live' ? 'Channel is not streaming' : 'Recording in progress' }}</h2>
          <p class="text-sm text-gray-400 mb-6">{{ notStreamingMessage }}</p>
          <button @click="exitEditor" class="btn-secondary">
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
            Go Back
          </button>
        </template>
      </div>
    </div>

    <!-- Main content -->
    <div v-else class="flex-1 flex min-h-0">
      <!-- Left: player + timeline + controls -->
      <div class="flex-1 flex flex-col p-3 gap-2 min-w-0">
        <!-- Player -->
        <div class="flex-1 min-h-0 relative">
          <EditorPlayer ref="player" class="absolute inset-0" @loop-seek="doSeek" @source-not-found="onSourceNotFound" />
        </div>
        <!-- Timeline + Controls card -->
        <div class="bg-white rounded-xl border border-gray-200 card-shadow shrink-0">
          <EditorTimeline @seek="onSeek" @drag-start="onDragStart" @drag-end="onDragEnd" />
          <div class="border-t border-gray-200" />
          <EditorControls
            @toggle-play="$refs.player?.togglePlay()"
            @skip="onSkip"
            @mark-in="markIn"
            @mark-out="markOut"
            @clear-selection="clearSelection"
            @go-live="goLive"
            @toggle-mute="toggleMute"
            @volume="onVolume"
            @zoom-in="zoomIn"
            @zoom-out="zoomOut"
            @zoom-fit="zoomFit"
          />
        </div>
      </div>

      <!-- Right: cut panel -->
      <div class="w-[320px] shrink-0 p-3 pl-0">
        <EditorCutPanel
          :export-title="exportTitle"
          :export-description="exportDescription"
          :selected-tags="selectedTags"
          :selected-category-ids="selectedCategoryIds"
          @update:exportTitle="exportTitle = $event"
          @update:exportDescription="exportDescription = $event"
          @update:selectedTags="selectedTags = $event"
          @update:selectedCategoryIds="selectedCategoryIds = $event"
          @add-cut="addCut"
          @remove-cut="removeCut"
          @clear-queue="clearQueue"
          @go-to-cut="goToCut"
          @edit-cut="editCut"
          @cancel-edit="cancelEdit"
          @nudge-in="nudgeIn"
          @nudge-out="nudgeOut"
          @preview-point="previewPoint"
          @loop-selection="toggleLoopSelection"
          @loop-all="toggleLoopAll"
          @show-formats="openFormatModal"
          @remove-export="removeExport"
        />
      </div>
    </div>

    <!-- Format selection modal -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showFormatModal" class="fixed inset-0 z-[600] flex items-center justify-center p-4">
          <div class="absolute inset-0 bg-black/50" />
          <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col animate-fade-in">
            <div class="flex items-center justify-between px-6 pt-6 pb-3 shrink-0">
              <div>
                <h3 class="text-lg font-semibold text-gray-900">Export "{{ exportTitle }}"</h3>
                <p class="text-sm text-gray-400 mt-0.5">Clip duration: <span class="font-data">{{ formatTimeMini(totalCutsDuration) }}</span></p>
              </div>
              <button @click="showFormatModal = false" class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div class="flex-1 overflow-y-auto px-6 pb-4">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button v-for="fmt in formats" :key="fmt.id" @click="toggleFormat(fmt)" :disabled="fmt.disabled"
                  :class="['flex items-start gap-3 p-3 rounded-lg border text-left transition-all', fmt.disabled ? 'opacity-40 cursor-not-allowed border-gray-100' : selectedFormats.includes(fmt.id) ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)]' : 'border-gray-200 hover:border-gray-300 cursor-pointer']"
                >
                  <div :class="['w-5 h-5 rounded border flex items-center justify-center shrink-0 mt-0.5', selectedFormats.includes(fmt.id) && !fmt.disabled ? 'bg-[var(--color-primary)] border-[var(--color-primary)]' : 'border-gray-300']">
                    <svg v-if="selectedFormats.includes(fmt.id) && !fmt.disabled" class="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <div class="min-w-0">
                    <p class="text-sm font-medium" :class="selectedFormats.includes(fmt.id) ? 'text-[var(--color-primary)]' : 'text-gray-900'">{{ fmt.name }}</p>
                    <p class="text-xs text-gray-400 mt-0.5">{{ fmt.description }}</p>
                    <p v-if="fmt.maxDuration" :class="['text-[11px] mt-1 font-medium', fmt.disabled ? 'text-red-500' : 'text-amber-500']">Max {{ formatTimeMini(fmt.maxDuration / 1000) }}</p>
                  </div>
                </button>
              </div>
            </div>
            <div class="px-6 py-4 border-t border-gray-100 flex items-center justify-between shrink-0">
              <span class="text-sm text-gray-400">{{ selectedFormats.length }} format(s) selected</span>
              <div class="flex gap-3">
                <button @click="showFormatModal = false" class="btn-secondary">Cancel</button>
                <button @click="doExport" :disabled="selectedFormats.length === 0 || exporting" class="btn-primary">
                  {{ exporting ? 'Exporting...' : 'Export' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import EditorPlayer from './components/EditorPlayer.vue'
import EditorTimeline from './components/EditorTimeline.vue'
import EditorControls from './components/EditorControls.vue'
import EditorCutPanel from './components/EditorCutPanel.vue'
import api from '../../services/api.js'
import websocket from '../../services/websocket.js'
import { notifySuccess, notifyError } from '../../services/notifications.js'

export default {
  name: 'VideoEditor',
  components: { EditorPlayer, EditorTimeline, EditorControls, EditorCutPanel },

  data() {
    return {
      liveNotStreaming: false,
      liveEnded: false,
      liveNoSession: false,
      notStreamingMessage: 'The live editor requires an active stream. Start the transcoder and begin streaming to use the editor.',
      exportTitle: '',
      exportDescription: '',
      selectedTags: [],
      selectedCategoryIds: [],
      selectedFormats: ['original_abr'],
      formats: [],
      showFormatModal: false
    }
  },

  computed: {
    ...mapState('editor', ['mode', 'sourceId', 'sourceData', 'currentTime', 'duration', 'playing', 'viewStart', 'viewDuration', 'markInTime', 'markOutTime', 'cuts', 'exporting', 'sessionExports']),
    ...mapGetters('editor', ['hasSelection', 'totalCutsDuration']),

    sourceTitle() {
      return this.sourceData?.title || this.sourceData?.meta?.name || 'Editor'
    },
  },

  created() {},

  async mounted() {
    const { rId, lId } = this.$route.query
    if (rId) {
      this.$store.commit('editor/SET_MODE', { mode: 'recording', sourceId: rId })
      await this.loadRecording(rId)
    } else if (lId) {
      this.$store.commit('editor/SET_MODE', { mode: 'live', sourceId: lId })
      await this.loadLive(lId)
    }

    this.loadFormats()

    window.addEventListener('keydown', this.handleKeydown)
    websocket.init()
    websocket.on('media:update', this.handleMediaUpdate)
    websocket.on('livestream:update', this.handleLivestreamUpdate)
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown)
    websocket.off('media:update', this.handleMediaUpdate)
    websocket.off('livestream:update', this.handleLivestreamUpdate)
    this.$store.commit('editor/RESET')
  },

  methods: {
    ...mapMutations('editor', ['SET_VIEW', 'SET_MARK_IN', 'SET_MARK_OUT', 'CLEAR_SELECTION', 'REMOVE_CUT', 'CLEAR_CUTS', 'SET_VOLUME', 'SET_MUTED', 'SET_EXPORTING', 'SET_EDITING_CUT', 'SET_LOOP_MODE', 'SET_PREVIEW_MODE', 'ADD_SESSION_EXPORT', 'UPDATE_SESSION_EXPORT', 'REMOVE_SESSION_EXPORT']),
    ...mapActions('editor', ['markIn', 'markOut', 'addCut']),

    async loadRecording(id) {
      try {
        const res = await api.get(`/recording/${id}`)
        const record = res.data?.record || res.data
        if (record.status === 'recording') {
          this.liveNotStreaming = true
          this.notStreamingMessage = 'This recording is still in progress. The editor will be available once the recording is complete.'
          this.$store.commit('editor/SET_SOURCE', { data: record, url: null })
          return
        }
        this.$store.commit('editor/SET_SOURCE', { data: record, url: this.$mediaUrl.recording(id) })
        this.$store.commit('editor/SET_START_DATE', record.transcoding?.channel?.started_at || record.created_at || null)
        this.exportTitle = record.title || ''
        this.selectedTags = record.tags || []
        this.selectedCategoryIds = (record.categories || []).map(c => typeof c === 'string' ? c : c._id?.toString())
      } catch (err) {
        notifyError(err?.message || 'Failed to load recording')
      }
    },

    async loadLive(id) {
      try {
        const res = await api.get(`/livestreams/${id}`)
        const live = res.data?.livestream || res.data
        // Allow editing as long as the channel has ever streamed — even after it
        // stops, the DVR session (within retention) keeps the manifest queryable.
        // Only block when the channel has never started yet.
        const everStreamed = !!live.transcoding?.channel?.started_at
        const isStreaming = live.transcoding?.channel?.status === 'streaming'
        if (!isStreaming && !everStreamed) {
          this.liveNotStreaming = true
          this.$store.commit('editor/SET_SOURCE', { data: live, url: null })
          return
        }
        // The editor only works against a pinned DVR session. Without one the
        // manifest URL can't resolve, so show "No session found" up front.
        if (!live.last_dvr_session?.dvrSessionId) {
          this.liveNoSession = true
          this.$store.commit('editor/SET_SOURCE', { data: live, url: null })
          return
        }
        // startDate is anchored to the first fragment's PDT in EditorPlayer.initLive()
        // once the DVR manifest loads. channel.started_at would be ~30-90s earlier
        // (transcoder warm-up) and break the export math.
        this.$store.commit('editor/SET_SOURCE', { data: live, url: this.$mediaUrl.live(id, live.last_dvr_session?.dvrSessionId) })
        this.exportTitle = live.meta?.name || ''
        this.selectedTags = live.tags || []
        this.selectedCategoryIds = (live.categories || []).map(c => typeof c === 'string' ? c : c._id?.toString())
      } catch (err) {
        notifyError(err?.message || 'Failed to load live')
      }
    },

    async loadFormats() {
      try {
        const res = await api.get('/formats', { silent: true })
        let formats = res.data?.formats || res.data || {}
        // If formats is an array, convert to object keyed by id
        if (Array.isArray(formats)) {
          const obj = {}
          formats.forEach(f => { if (f.id) obj[f.id] = f })
          formats = obj
        }
        const order = ['original_abr', 'original', 'tiktok', 'instagram_reels', 'youtube_shorts', 'instagram_feed', 'twitter', 'mp3']
        this.formats = order.filter(key => formats[key]).map(key => {
          const f = formats[key]
          return { ...f, id: f.id || key, disabled: false }
        })
      } catch (err) {
        console.error('[Editor] Failed to load formats:', err)
      }
    },

    // Seek — DVR manifest lists every segment with real PDTs, so hls.js seeks natively.
    doSeek(time) { this.$refs.player?.seek(time) },
    onSeek(time) { this.$refs.player?.seek(time) },
    onDragStart() { this.$refs.player?.setUserDragging(true) },
    onDragEnd() { this.$refs.player?.setUserDragging(false) },
    onSkip(seconds) {
      const newTime = Math.max(0, Math.min(this.duration, this.currentTime + seconds))
      this.doSeek(newTime)
    },
    onVolume(v) { this.SET_VOLUME(v) },
    goLive() {
      this.SET_LOOP_MODE(null)
      this.SET_PREVIEW_MODE({ mode: null, end: null })
      this.$refs.player?.goLive()
    },
    toggleMute() { this.SET_MUTED(!this.$store.state.editor.muted) },

    // Zoom
    zoomIn() {
      const newDuration = Math.max(5, this.viewDuration * 0.7)
      const center = this.viewStart + this.viewDuration / 2
      this.SET_VIEW({ start: Math.max(0, center - newDuration / 2), duration: newDuration })
    },
    zoomOut() {
      const newDuration = Math.min(this.duration || 3600, this.viewDuration * 1.4)
      const center = this.viewStart + this.viewDuration / 2
      this.SET_VIEW({ start: Math.max(0, center - newDuration / 2), duration: newDuration })
    },
    zoomFit() {
      this.SET_VIEW({ start: 0, duration: this.duration || 60 })
    },

    // Selection
    clearSelection() { this.CLEAR_SELECTION() },
    nudgeIn(delta) {
      if (this.markInTime === null) return
      const newTime = Math.max(0, this.markInTime + delta)
      this.SET_MARK_IN(newTime)
      this.$refs.player?.seek(newTime)
    },
    nudgeOut(delta) {
      if (this.$store.state.editor.markOutTime === null) return
      const newTime = this.$store.state.editor.markOutTime + delta
      this.SET_MARK_OUT(newTime)
      this.$refs.player?.seek(newTime)
    },

    // Cuts
    removeCut(index) {
      this.REMOVE_CUT(index)
      if (this.$store.state.editor.editingCutIndex === index) {
        this.SET_EDITING_CUT(null)
        this.CLEAR_SELECTION()
      } else if (this.$store.state.editor.editingCutIndex > index) {
        this.SET_EDITING_CUT(this.$store.state.editor.editingCutIndex - 1)
      }
    },
    clearQueue() {
      this.CLEAR_CUTS()
      this.SET_EDITING_CUT(null)
      this.CLEAR_SELECTION()
    },
    goToCut(cut) { this.doSeek(cut.inTime) },
    editCut(index) {
      const cut = this.cuts[index]
      this.SET_MARK_IN(cut.inTime)
      this.SET_MARK_OUT(cut.outTime)
      this.SET_EDITING_CUT(index)
      this.doSeek(cut.inTime)
    },
    cancelEdit() {
      this.SET_EDITING_CUT(null)
      this.CLEAR_SELECTION()
    },

    // Preview & Loop
    async previewPoint(point) {
      const player = this.$refs.player
      if (!player) return
      if (this.$store.state.editor.previewMode === point && this.playing) {
        player.pause()
        this.SET_PREVIEW_MODE({ mode: null, end: null })
        return
      }
      const time = point === 'in' ? this.markInTime : this.markOutTime
      if (time === null) return
      const start = Math.max(0, time - 2)
      const end = Math.min(this.duration, time + 2)
      this.SET_PREVIEW_MODE({ mode: point, end })
      this.SET_LOOP_MODE(null)
      await this.doSeek(start)
      player.play()
    },
    async toggleLoopSelection() {
      if (!this.$store.getters['editor/hasSelection']) return
      this.SET_PREVIEW_MODE({ mode: null, end: null })
      if (this.$store.state.editor.loopMode === 'selection' && this.playing) {
        this.$refs.player?.pause()
        this.SET_LOOP_MODE(null)
      } else {
        this.SET_LOOP_MODE('selection')
        await this.doSeek(this.markInTime)
        this.$refs.player?.play()
      }
    },
    async toggleLoopAll() {
      if (!this.cuts.length) return
      this.SET_PREVIEW_MODE({ mode: null, end: null })
      if (this.$store.state.editor.loopMode === 'all') {
        this.SET_LOOP_MODE(null)
        this.$refs.player?.pause()
      } else {
        this.SET_LOOP_MODE('all')
        await this.doSeek(this.cuts[0].inTime)
        this.$refs.player?.play()
      }
    },

    // Formats
    toggleFormat(fmt) {
      if (fmt.disabled) return
      const idx = this.selectedFormats.indexOf(fmt.id)
      if (idx >= 0) this.selectedFormats.splice(idx, 1)
      else this.selectedFormats.push(fmt.id)
    },

    openFormatModal() {
      if (!this.cuts.length) return
      this.selectedFormats = ['original_abr', 'original_mp4']
      const totalMs = this.$store.getters['editor/totalCutsDuration'] * 1000
      this.formats.forEach(f => {
        f.disabled = f.maxDuration ? totalMs > f.maxDuration : false
      })
      this.selectedFormats = this.selectedFormats.filter(
        id => !this.formats.find(f => f.id === id && f.disabled)
      )
      this.showFormatModal = true
    },

    // Export
    async doExport() {
      if (!this.exportTitle?.trim() || this.selectedFormats.length === 0) return
      this.SET_EXPORTING(true)
      try {
        const endpoint = this.mode === 'recording'
          ? `/recording/${this.sourceId}/export`
          : `/livestreams/${this.sourceId}/export`

        const tags = this.selectedTags.map(t => t._id || t)

        const startDate = this.$store.state.editor.startDate
        const cutsPayload = this.cuts.map(c => {
          if (startDate) {
            return {
              startTime: new Date(startDate.getTime() + c.inTime * 1000).toISOString(),
              endTime: new Date(startDate.getTime() + c.outTime * 1000).toISOString()
            }
          }
          return { startTime: c.inTime, endTime: c.outTime }
        })
        console.log(`[Editor] Export (${this.mode}) startDate=${startDate?.toISOString()} cuts=`,
          this.cuts.map((c, i) => `${c.inTime.toFixed(2)}s→${c.outTime.toFixed(2)}s = ${cutsPayload[i].startTime}→${cutsPayload[i].endTime}`))

        const payload = {
          title: this.exportTitle,
          description: this.exportDescription,
          tags,
          categories: this.selectedCategoryIds,
          formats: this.selectedFormats,
          cuts: cutsPayload
        }
        // Live exports pin to the DVR session that produced the manifest the
        // user just edited against — same id we passed to $mediaUrl.live() on
        // load. Recordings export against the recording asset directly, so
        // no session id is needed there.
        if (this.mode === 'live') {
          const dvrSessionId = this.sourceData?.last_dvr_session?.dvrSessionId
          if (dvrSessionId) payload.dvrSessionId = dvrSessionId
        }
        const res = await api.post(endpoint, payload)

        const response = res.data?.export || res.data || {}
        this.ADD_SESSION_EXPORT({
          id: response._id || response.id || Date.now(),
          media_id: response.media_id || null,
          title: this.exportTitle,
          formats: [...this.selectedFormats],
          status: response.status || 'queue',
          progress: 0
        })

        // Reset after export
        this.CLEAR_CUTS()
        this.SET_EDITING_CUT(null)
        this.CLEAR_SELECTION()
        this.exportTitle = this.sourceTitle || ''
        this.exportDescription = ''
        this.selectedTags = []

        notifySuccess('Export started successfully')
        this.showFormatModal = false
      } catch (err) {
        notifyError(err?.message || 'Export failed')
      } finally {
        this.SET_EXPORTING(false)
      }
    },

    handleMediaUpdate(data) {
      const mediaId = data._id?.toString()
      if (!mediaId) return
      this.UPDATE_SESSION_EXPORT({
        id: mediaId,
        data: {
          status: data.file_object?.status,
          progress: data.file_object?.progress
        }
      })
    },

    // Live channel status changes (start/stop). When the channel transitions
    // to 'stopped' mid-session, surface a non-blocking banner: the DVR session
    // id was pinned at load and stays valid for the rest of this editing
    // session (cuts/exports against it still work). The user can keep editing
    // the captured content; reloading is only needed to pick up a new live
    // session or the finalized recording.
    handleLivestreamUpdate(data) {
      if (this.mode !== 'live') return
      if (data._id?.toString() !== this.sourceId?.toString()) return
      const current = this.sourceData
      if (!current) return
      const next = { ...current }
      if (data.status?.livestream != null) next.status = data.status.livestream
      if (data.status?.is_running != null) next.is_running = data.status.is_running
      next.transcoding = { ...(current.transcoding || {}) }
      if (current.transcoding?.input) {
        next.transcoding.input = { ...current.transcoding.input }
        if (data.status?.input != null) next.transcoding.input.status = data.status.input
      }
      if (current.transcoding?.channel) {
        next.transcoding.channel = { ...current.transcoding.channel }
        if (data.status?.channel != null) next.transcoding.channel.status = data.status.channel
        if (data.status?.channel === 'stopped' && !next.transcoding.channel.ended_at) {
          next.transcoding.channel.ended_at = new Date().toISOString()
        }
      }
      if (data.status?.recording != null) {
        next.transcoding.recording = data.status.recording
      }
      this.$store.commit('editor/SET_SOURCE', { data: next, url: this.$store.state.editor.sourceUrl })

      // Surface the "stream ended" banner once the channel stops mid-session.
      // Don't auto-pause — the user can keep playing/scrubbing the captured
      // DVR content; playback naturally ends when it runs out of segments.
      if (data.status?.channel === 'stopped' && !this.liveEnded) {
        this.liveEnded = true
      }
    },

    removeExport(exp) {
      this.REMOVE_SESSION_EXPORT(exp._id || exp.media_id || exp.id)
    },

    // Keyboard shortcuts
    handleKeydown(e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return
      switch (e.key) {
        case ' ': e.preventDefault(); this.$refs.player?.togglePlay(); break
        case 'i': case 'I': this.markIn(); break
        case 'o': case 'O': this.markOut(); break
        case 'ArrowLeft': e.preventDefault(); this.onSkip(-5); break
        case 'ArrowRight': e.preventDefault(); this.onSkip(5); break
      }
    },

    exitEditor() {
      this.$router.back()
    },

    reloadPage() {
      window.location.reload()
    },

    onSourceNotFound() {
      // "No session found" is a DVR concept — recordings play their own asset.
      if (this.mode !== 'live') {
        notifyError('Recording video not found')
        return
      }
      if (this.liveNoSession) return
      this.liveNoSession = true
      this.$refs.player?.pause()
    },

    formatTimeMini(s) {
      if (!s || isNaN(s)) return '0:00'
      const m = Math.floor(s / 60)
      const sec = Math.floor(s % 60)
      return `${m}:${String(sec).padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: opacity 0.2s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
</style>
